import React, { useState } from 'react';
import './ContactPage.css';
import Navbar from './Navbar';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: '',
    budget: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [touchedFields, setTouchedFields] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBlur = (field) => {
    setTouchedFields({
      ...touchedFields,
      [field]: true
    });
  };

  const sendToWhatsApp = () => {
    const phoneNumber = '94762658438';
    
    // Format the message with all form data
    const message = `
📸 *New Photography Appointment Request*

👤 *Full Name:* ${formData.name}
📧 *Email:* ${formData.email}
📞 *Phone:* ${formData.phone || 'Not provided'}
📅 *Session Type:* ${formData.eventType}
📅 *Preferred Date:* ${formData.eventDate || 'Not specified'}
💰 *Selected Package:* ${formData.budget || 'Not specified'}

📋 *Project Details:*
${formData.message}

---
*Sent from Dilan Munasingha Photography Website*
    `.trim();

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp after a short delay for better UX
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    if (!formData.name || !formData.email || !formData.eventType || !formData.message) {
      setSubmitMessage('❌ Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitMessage('❌ Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }

    setSubmitMessage('📱 Opening WhatsApp to send your message...');
    
    // Send to WhatsApp
    sendToWhatsApp();
    
    // Reset form after delay
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        eventDate: '',
        message: '',
        budget: ''
      });
      setTouchedFields({});
      setSubmitMessage('');
    }, 3000);
  };

  const eventTypes = [
    'Wedding Photography',
    'Birthday Celebration',
    'Engagement Session',
    'Portrait Session',
    'Family Photos',
    'Commercial Photography',
    'Event Coverage',
    'Other (Please specify in message)'
  ];

  const budgetOptions = [
    { value: 'Basic', label: 'Basic Package' },
    { value: 'Professional', label: 'Professional Package' },
    { value: 'Premium', label: 'Premium Package' },
    { value: 'custom', label: 'Custom Quote' }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <div className="contact-hero">
        <div className="hero-overlay">
          <h1>Let's Create Something Beautiful</h1>
          <p>Ready to capture your special moments? Get in touch and let's discuss your photography needs.</p>
        </div>
      </div>

      <div className="contact-container">
        <div className="contact-content">
          {/* Contact Information */}
          <div className="contact-info">
            <h2>Get In Touch</h2>
            <p className="contact-description">
              Whether you're planning a wedding, need professional portraits, or want to capture special family moments,
              I'd love to hear about your vision. Fill out the form and I'll get back to you within 24 hours.
            </p>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div>
                  <h3>Email</h3>
                  <p>Dilanmunasingha@gmail.com</p>
                  <small>Response within 24 hours</small>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div>
                  <h3>Phone & WhatsApp</h3>
                  <p>+94 76 265 8438</p>
                  <small>Available 9AM - 6PM</small>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <h3>Studio Location</h3>
                  <p>Horana, Sri Lanka</p>
                  <small>Available for travel nationwide</small>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">🕒</div>
                <div>
                  <h3>Working Hours</h3>
                  <p>Monday - Friday: 9AM - 6PM<br />Weekends: By appointment only</p>
                </div>
              </div>
            </div>

            <div className="social-links minimal">
              <h3>Social Profile</h3>
              <div className="social-icons">
                <a
                  href="https://www.instagram.com/dilanmunasinghaphotography"
                  className="social-btn instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="btn-icon">📷</span>
                  Instagram
                </a>
                <a
                  href="https://web.facebook.com/dilanmunasinghaphotography"
                  className="social-btn facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="btn-icon">📘</span>
                  Facebook
                </a>
                <a
                  href="https://www.linkedin.com/in/dilan-munasingha-60022b2a7"
                  className="social-btn linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="btn-icon">💼</span>
                  LinkedIn
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <h2>Send a Message</h2>
              
              {submitMessage && (
                <div className={`success-message ${submitMessage.includes('❌') ? 'error-message' : ''}`}>
                  {submitMessage}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={() => handleBlur('name')}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => handleBlur('email')}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+94 000000"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="eventType">Type of Session</label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    onBlur={() => handleBlur('eventType')}
                    required
                  >
                    <option value="">Select session type</option>
                    {eventTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventDate">Preferred Date</label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="budget">Select Package</label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                  >
                    <option value="">Select a package</option>
                    {budgetOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Tell Me About Your Project</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={() => handleBlur('message')}
                  placeholder="Please share details about your event, vision, specific requests, or any questions you might have..."
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Opening WhatsApp...' : 'Send'}
              </button>

              <p style={{
                marginTop: '1rem',
                fontSize: '0.9rem',
                color: '#666',
                textAlign: 'center',
                opacity: 0.8
              }}>
                ✨ Your message will be sent directly to WhatsApp for fastest response
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
